import React, { useEffect, useState } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { canSeeSound, isCrewBoss } from '../../utils/soundRules';
import { field, labelStyle, errorStyle, hintStyle } from '../../components/formStyles';

export const ADMIN_EMAIL = 'dillon@dillon.is';

const Box = ({ children }) => (
    <div style={{ minHeight: '60vh', padding: '60px 20px' }}>
        <div style={{ maxWidth: '440px', margin: '0 auto', border: '1px solid var(--color-gold)', background: 'rgba(20,20,20,0.8)', padding: '36px' }}>{children}</div>
    </div>
);

const linkButton = { background: 'none', border: 'none', color: '#c89b3c', cursor: 'pointer', padding: 0, font: 'inherit' };

// who may open which part: the staff table (migration 0005) decides, see src/utils/soundRules.js
const ALLOWED = {
    admin: (s) => Boolean(s && s.active && s.is_admin),
    crew: isCrewBoss,
    sound: canSeeSound,
};

/**
 * Sign-in gate for staff: Google, email + password, or an emailed sign-in link.
 * need="admin" (default) for /admin, "crew" for the crew page (admins and the sound lead),
 * "sound" for the engineers' page. Renders children({ session, staff }) once allowed.
 */
const AdminGate = ({ children, need = 'admin' }) => {
    const sound = need === 'sound';
    const [session, setSession] = useState(undefined); // undefined = checking
    const [staff, setStaff] = useState(undefined); // the signed-in person's staff row, null if none
    const [email, setEmail] = useState(sound ? '' : ADMIN_EMAIL);
    const [password, setPassword] = useState('');
    const [mode, setMode] = useState(sound ? 'link' : 'password'); // 'password' | 'link'
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        return () => meta.remove();
    }, []);

    useEffect(() => {
        if (!supabase) { setSession(null); return undefined; }
        let live = true;
        const start = async () => {
            // ?login=<token hash> from the emailed sign-in link (server/sound.mjs sendLoginLink)
            const params = new URLSearchParams(window.location.search);
            // a Google account with no staff login comes back with an error (sign-ups are closed)
            const hash = new URLSearchParams(window.location.hash.slice(1));
            const oauthError = params.get('error_description') || hash.get('error_description');
            if (oauthError) {
                ['error', 'error_code', 'error_description'].forEach((k) => params.delete(k));
                const rest = params.toString();
                window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
                if (live) setError(/signup|sign-up|not allowed/i.test(oauthError) ? 'That Google account is not on the staff list. Use the Google account of the email address you were added with, or get a sign-in link by email.' : oauthError);
            }
            const token = params.get('login');
            if (token) {
                params.delete('login');
                const rest = params.toString();
                window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
                const { error: err } = await supabase.auth.verifyOtp({ token_hash: token, type: 'magiclink' });
                if (err && live) setError('That sign-in link has expired or was already used. Ask for a new one below.');
            }
            const { data } = await supabase.auth.getSession();
            if (live) setSession(data.session);
        };
        start();
        const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
        return () => { live = false; sub.subscription.unsubscribe(); };
    }, []);

    const userEmail = session?.user?.email?.toLowerCase() || null;
    useEffect(() => {
        if (!userEmail) return undefined;
        let live = true;
        // RLS lets everyone read their own row only
        supabase.from('staff').select('*').eq('email', userEmail).maybeSingle()
            .then(({ data }) => { if (live) setStaff({ email: userEmail, row: data || null }); });
        return () => { live = false; };
    }, [userEmail]);

    const signInWithGoogle = async () => {
        setError(null);
        setBusy(true);
        const { error: err } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                // back to the page they were on (/admin, /admin/crew or /sound)
                redirectTo: `${window.location.origin}${window.location.pathname}`,
                // no hd hint: staff sign in with Gmail and other addresses too, not only dillon.is
                queryParams: { prompt: 'select_account' },
            },
        });
        if (err) { setError(err.message); setBusy(false); }
    };

    const signIn = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            if (mode === 'password') {
                const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
                if (err) setError(err.message === 'Invalid login credentials' ? 'Wrong email or password.' : err.message);
            } else {
                const res = await fetch('/api/admin/action', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'login_link', email: email.trim(), to: sound ? 'sound' : 'admin', path: window.location.pathname }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) setError(data.error || `Could not send the link (${res.status})`);
                else setSent(true);
            }
        } finally {
            setBusy(false);
        }
    };

    if (!supabase) return <Box><p style={errorStyle}>Admin is not configured (missing Supabase keys).</p></Box>;
    const checking = session === undefined || (session && staff?.email !== userEmail);
    if (checking) return <Box><p className="text-gold" style={{ letterSpacing: '3px' }}>CHECKING…</p></Box>;

    if (session && !ALLOWED[need](staff.row)) {
        return (
            <Box>
                <p style={errorStyle}>{session.user.email} {sound ? 'is not on the Dillon sound crew.' : 'is not allowed here.'}</p>
                {sound && <p style={hintStyle}>Ask Dillon or the sound lead to add you.</p>}
                <button type="button" className="btn btn-outline" onClick={() => supabase.auth.signOut()} style={{ marginTop: '16px' }}>Sign out</button>
            </Box>
        );
    }

    if (!session) {
        return (
            <Box>
                <p style={{ color: '#c89b3c', letterSpacing: '4px', fontSize: '12px', textTransform: 'uppercase', margin: '0 0 8px' }}>{sound ? 'Dillon sound crew' : 'Dillon staff'}</p>
                <h1 style={{ fontFamily: 'var(--font-heading)', color: '#f0e6cc', fontSize: '32px', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 20px' }}>Sign in</h1>
                <button type="button" className="btn btn-primary" onClick={signInWithGoogle} disabled={busy} style={{ width: '100%', marginBottom: '18px' }}>
                    Sign in with Google
                </button>
                <p style={{ ...hintStyle, textAlign: 'center', margin: '0 0 18px' }}>
                    {sound ? 'Use the Google account of the email you were added with. Or get a sign-in link by email, no password needed:' : 'Use the Google account of your staff email. Or, if you need to:'}
                </p>
                {sent ? (
                    <p style={{ color: '#ccc', lineHeight: 1.6 }}>
                        If {email.trim()} is on the {sound ? 'sound crew' : 'staff list'}, a sign-in link is on its way. Check your inbox; the link works once, within an hour.
                    </p>
                ) : (
                    <form onSubmit={signIn} style={{ display: 'grid', gap: '14px' }}>
                        <div>
                            <label htmlFor="admin-email" style={labelStyle}>Email</label>
                            <input id="admin-email" type="email" required autoComplete="username" spellCheck="false" value={email} onChange={(e) => setEmail(e.target.value)} style={field} />
                        </div>
                        {mode === 'password' && (
                            <div>
                                <label htmlFor="admin-password" style={labelStyle}>Password</label>
                                <input id="admin-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={field} />
                            </div>
                        )}
                        {error && <p style={errorStyle}>{error}</p>}
                        <button type="submit" className="btn btn-primary" disabled={busy}>
                            {busy ? (mode === 'password' ? 'Signing in…' : 'Sending…') : mode === 'password' ? 'Sign In' : 'Email Me a Sign-in Link'}
                        </button>
                        {!sound && (
                            <p style={hintStyle}>
                                {mode === 'password' ? (
                                    <>No password, or forgot it? <button type="button" onClick={() => { setMode('link'); setError(null); }} style={linkButton}>Email me a sign-in link</button></>
                                ) : (
                                    <button type="button" onClick={() => { setMode('password'); setError(null); }} style={linkButton}>Use a password instead</button>
                                )}
                            </p>
                        )}
                    </form>
                )}
            </Box>
        );
    }

    return children({ session, staff: staff.row });
};

export default AdminGate;
